export interface IAiToolResultField {
    key: string;
    label: string;
    value: unknown;
}

export type AiToolResultSection =
    | {
          kind: "fields";
          key: string;
          title: string | null;
          fields: IAiToolResultField[];
      }
    | {
          kind: "table";
          key: string;
          title: string;
          columns: string[];
          rows: Record<string, unknown>[];
      }
    | { kind: "list"; key: string; title: string; values: string[] }
    | { kind: "raw"; key: string; title: string; json: string };

const isScalar = (value: unknown): boolean =>
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isScalarObject = (value: unknown): value is Record<string, unknown> =>
    isPlainObject(value) && Object.values(value).every(isScalar);

const isObjectList = (value: unknown): value is Record<string, unknown>[] =>
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => isScalarObject(item));

/**
 * "account_compliant_percent" reads as "Account compliant percent". Tool output
 * keys are English and stable, so naming them from the key itself is honest and
 * keeps a new tool field from needing a translation before it can be shown.
 */
export const humanizeToolResultKey = (key: string): string => {
    const words = key.replace(/^_+/, "").replace(/_/g, " ").trim();

    return words === "" ? key : words.charAt(0).toUpperCase() + words.slice(1);
};

const columnsOf = (rows: Record<string, unknown>[]): string[] => {
    const columns: string[] = [];

    for (const row of rows) {
        for (const column of Object.keys(row)) {
            if (!columns.includes(column)) {
                columns.push(column);
            }
        }
    }

    return columns;
};

const toField = (key: string, value: unknown): IAiToolResultField => ({
    key,
    label: humanizeToolResultKey(key),
    value,
});

/**
 * Splits a tool's output into the blocks the result card renders. The shape of
 * the data decides the block, not the name of the tool: scalars group into a
 * field list, a list of uniform objects becomes a table, and anything deeper
 * than that is shown as-is rather than guessed at.
 *
 * This is what keeps the card from drifting: a tool that grows a field renders
 * it the next deploy, with nothing here to update.
 */
export const buildToolResultSections = (
    output: Record<string, unknown>,
): AiToolResultSection[] => {
    const sections: AiToolResultSection[] = [];
    const leading: IAiToolResultField[] = [];

    for (const [key, value] of Object.entries(output)) {
        if (isScalar(value)) {
            leading.push(toField(key, value));
            continue;
        }

        if (isObjectList(value)) {
            sections.push({
                kind: "table",
                key,
                title: humanizeToolResultKey(key),
                columns: columnsOf(value),
                rows: value,
            });
            continue;
        }

        if (Array.isArray(value) && value.every(isScalar)) {
            sections.push({
                kind: "list",
                key,
                title: humanizeToolResultKey(key),
                values: value.map((item) =>
                    item === null ? "—" : String(item),
                ),
            });
            continue;
        }

        if (isScalarObject(value)) {
            sections.push({
                kind: "fields",
                key,
                title: humanizeToolResultKey(key),
                fields: Object.entries(value).map(([name, item]) =>
                    toField(name, item),
                ),
            });
            continue;
        }

        sections.push({
            kind: "raw",
            key,
            title: humanizeToolResultKey(key),
            json: JSON.stringify(value, null, 2),
        });
    }

    return leading.length > 0
        ? [
              { kind: "fields", key: "_summary", title: null, fields: leading },
              ...sections,
          ]
        : sections;
};
